// pages/student/components/Specialization.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Fade,
  Zoom,
  TextField,
  InputAdornment,
  CircularProgress,
  Stack,
  Paper,
} from "@mui/material";
import {
  Search,
  School,
  People,
  AccessTime,
  Star,
  PlayCircle,
  ArrowForward,
  TrendingUp,
  Security,
  Code,
  Psychology,
  Storage,
  RocketLaunch,
  Verified,
} from "@mui/icons-material";
import { sendEmail, postData } from "../../../services/Request";
import Swal from "sweetalert2";
import AI from "../../../assets/Poster/AI.png";
import MS from "../../../assets/Poster/MS.png";
import SD from "../../../assets/Poster/SD.png";
import CS from "../../../assets/Poster/Cyber-Security.jpeg";
import "../../../styles/Specialization.css";

const courses = [
  {
    id: 1,
    img: MS,
    level: "Beginner",
    levelColor: "#2BAD65",
    levelBg: "rgba(43, 173, 101, 0.12)",
    courses: "4 Courses",
    title: "Microsoft Dynamics",
    icon: <Storage />,
    students: 1250,
    rating: 4.8,
    duration: "6 Weeks",
    description:
      "Master Microsoft Dynamics 365 and learn to streamline business processes with this comprehensive course.",
    tags: ["ERP", "CRM", "Business"],
    color: "#2BAD65",
  },
  {
    id: 2,
    img: SD,
    level: "Intermediate",
    levelColor: "#A0EF48",
    levelBg: "rgba(160, 239, 72, 0.12)",
    courses: "4 Courses",
    title: "Software Development",
    icon: <Code />,
    students: 890,
    rating: 4.7,
    duration: "8 Weeks",
    description:
      "Build robust applications with modern software development practices and industry-standard tools.",
    tags: ["Full Stack", "DevOps", "Agile"],
    color: "#A0EF48",
  },
  {
    id: 3,
    img: AI,
    level: "Advance",
    levelColor: "#FFD93D",
    levelBg: "rgba(255, 217, 61, 0.12)",
    courses: "4 Courses",
    title: "Artificial Intelligence",
    icon: <Psychology />,
    students: 2100,
    rating: 4.9,
    duration: "10 Weeks",
    description:
      "Dive deep into AI, machine learning, and neural networks with hands-on projects and real-world applications.",
    tags: ["ML", "Deep Learning", "NLP"],
    color: "#FFD93D",
  },
  {
    id: 4,
    img: CS,
    level: "Intermediate",
    levelColor: "#FF6B6B",
    levelBg: "rgba(255, 107, 107, 0.12)",
    courses: "4 Courses",
    title: "Cyber Security",
    icon: <Security />,
    students: 670,
    rating: 4.6,
    duration: "8 Weeks",
    description:
      "Protect systems and networks from cyber threats with advanced security measures and ethical hacking techniques.",
    tags: ["Network Security", "Ethical Hacking", "Compliance"],
    color: "#FF6B6B",
  },
];

const Specialization = () => {
  const nav = useNavigate();
  const [loader, setLoader] = useState(null);
  const [userInfo, setUserInfo] = useState({});
  const [courseName, setCourseName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (!user || user === "null") {
      nav("/");
    } else {
      try {
        const data = JSON.parse(user);
        setUserInfo(data);
      } catch (error) {
        console.error("Error parsing user data:", error);
        nav("/");
      }
    }
  }, [nav]);

  const filteredCourses = courses.filter((course) =>
    course.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleClickButton = (title) => {
    setLoader(title);
    setCourseName(title);

    const user = localStorage.getItem("user");
    if (!user || user === "null") {
      nav("/usersignup");
      setLoader(null);
      return;
    }

    const applyCourse = {
      userId: userInfo.userId,
      courseName: title,
      courseLevel: "Beginner",
    };

    const mailStudent = {
      fullName: `${userInfo.firstname || ""} ${userInfo.lastname || ""}`,
      email: userInfo.email || "",
      courseName: title,
    };

    const mailAdmin = {
      fullName: `${userInfo.firstname || ""} ${userInfo.lastname || ""}`,
      email: "wavepalmtechng@gmail.com",
      courseName: title,
    };

    postData("selectcourse", applyCourse)
      .then((response) => {
        setLoader(null);
        if (response.status === true) {
          sendEmail("/enrollMail.php", mailStudent);
          sendEmail("/enrollMailAdmin.php", mailAdmin);
          Swal.fire({
            title: "🎉 Congratulations!",
            text: `You have successfully enrolled in ${title}`,
            icon: "success",
            confirmButtonColor: "#2BAD65",
            background: "#ffffff",
            color: "#1a1a1a",
            confirmButtonText: "Join WhatsApp Group",
          }).then(() => {
            window.open(
              "https://chat.whatsapp.com/L0362sTsqwiHrv23KrMscn",
              "_blank",
            );
          });
        } else if (response.status === false) {
          Swal.fire({
            title: "⚠️ Already Enrolled",
            text: response.message || "You are already enrolled in this course",
            icon: "warning",
            confirmButtonColor: "#FFD93D",
            background: "#ffffff",
            color: "#1a1a1a",
          });
        } else {
          Swal.fire({
            title: "❌ Error",
            text: "You cannot enroll in this course at the moment",
            icon: "error",
            confirmButtonColor: "#FF6B6B",
            background: "#ffffff",
            color: "#1a1a1a",
          });
        }
      })
      .catch((error) => {
        setLoader(null);
        Swal.fire({
          title: "❌ Error",
          text: "Something went wrong. Please try again.",
          icon: "error",
          confirmButtonColor: "#FF6B6B",
          background: "#ffffff",
          color: "#1a1a1a",
        });
        console.error("Enrollment error:", error);
      });
  };

  return (
    <Box
      sx={{
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        backgroundColor: "#F7F8F2",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#1a1a1a",
            mb: 1,
          }}
        >
          Explore Courses
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "rgba(26, 26, 26, 0.5)",
          }}
        >
          Discover and enroll in courses that match your learning goals
        </Typography>
      </Box>

      {/* Search Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 4,
          backgroundColor: "#ffffff",
          borderRadius: 2,
          border: "1px solid rgba(43, 173, 101, 0.06)",
        }}
      >
        <TextField
          fullWidth
          placeholder="Search for a course..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "rgba(26, 26, 26, 0.3)" }} />
              </InputAdornment>
            ),
            sx: {
              "& fieldset": {
                borderColor: "rgba(43, 173, 101, 0.1)",
                borderRadius: 2,
              },
              "&:hover fieldset": {
                borderColor: "rgba(43, 173, 101, 0.2)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#2BAD65",
              },
            },
          }}
          sx={{
            "& .MuiInputBase-input": {
              py: 1.5,
              color: "#1a1a1a",
              "&::placeholder": {
                color: "rgba(26, 26, 26, 0.3)",
              },
            },
          }}
        />
      </Paper>

      {/* Course Grid */}
      <Grid container spacing={3}>
        {filteredCourses.length > 0 ? (
          filteredCourses.map((course, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={course.id}>
              <Fade in timeout={400 + index * 100}>
                <Card
                  onMouseEnter={() => setHoveredCard(course.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "#ffffff",
                    borderRadius: 3,
                    border: "1px solid rgba(43, 173, 101, 0.06)",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    overflow: "hidden",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 12px 40px rgba(43, 173, 101, 0.1)`,
                      borderColor: course.color,
                    },
                  }}
                >
                  {/* Card Image */}
                  <Box sx={{ position: "relative" }}>
                    <CardMedia
                      component="img"
                      height="180"
                      image={course.img}
                      alt={course.title}
                      sx={{
                        transition: "transform 0.5s ease",
                        transform:
                          hoveredCard === course.id
                            ? "scale(1.05)"
                            : "scale(1)",
                      }}
                    />
                    {/* Level Badge */}
                    <Chip
                      label={course.level}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        backgroundColor: course.levelBg,
                        color: course.levelColor,
                        fontWeight: 700,
                        fontSize: "0.6rem",
                        border: `1px solid ${course.levelColor}30`,
                        backdropFilter: "blur(4px)",
                      }}
                    />
                    {/* Popular Badge */}
                    {course.rating >= 4.8 && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 12,
                          left: 12,
                          backgroundColor: "rgba(255, 215, 0, 0.9)",
                          color: "#000",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 50,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontSize: "0.55rem",
                          fontWeight: 700,
                        }}
                      >
                        <Star sx={{ fontSize: 12 }} />
                        POPULAR
                      </Box>
                    )}
                  </Box>

                  <CardContent
                    sx={{
                      p: 2.5,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Title & Icon */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          color: "#1a1a1a",
                          fontSize: "1rem",
                          flex: 1,
                        }}
                      >
                        {course.title}
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: course.levelBg,
                          borderRadius: "50%",
                          width: 32,
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: course.color,
                          flexShrink: 0,
                          ml: 1,
                          transition: "all 0.3s ease",
                          transform:
                            hoveredCard === course.id
                              ? "scale(1.1) rotate(10deg)"
                              : "scale(1)",
                        }}
                      >
                        {course.icon}
                      </Box>
                    </Box>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(26, 26, 26, 0.6)",
                        fontSize: "0.8rem",
                        lineHeight: 1.6,
                        mb: 2,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        flex: 1,
                      }}
                    >
                      {course.description}
                    </Typography>

                    {/* Stats */}
                    <Stack
                      direction="row"
                      spacing={1.5}
                      sx={{ mb: 2, flexWrap: "wrap", gap: 0.5 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <People
                          sx={{ fontSize: 14, color: "rgba(26,26,26,0.3)" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26,26,26,0.4)" }}
                        >
                          {course.students}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Star sx={{ fontSize: 14, color: "#FFD93D" }} />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26,26,26,0.4)" }}
                        >
                          {course.rating}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <AccessTime
                          sx={{ fontSize: 14, color: "rgba(26,26,26,0.3)" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26,26,26,0.4)" }}
                        >
                          {course.duration}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Tags */}
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{ flexWrap: "wrap", gap: 0.5, mb: 2 }}
                    >
                      {course.tags.map((tag, idx) => (
                        <Chip
                          key={idx}
                          label={tag}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(43, 173, 101, 0.04)",
                            color: "rgba(26, 26, 26, 0.4)",
                            fontSize: "0.55rem",
                            height: 20,
                            border: "1px solid rgba(43, 173, 101, 0.06)",
                          }}
                        />
                      ))}
                    </Stack>

                    {/* Course Count & Enroll Button */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mt: "auto",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(26, 26, 26, 0.3)",
                          fontSize: "0.6rem",
                        }}
                      >
                        {course.courses} available
                      </Typography>

                      {loader !== course.title ? (
                        <Button
                          variant="contained"
                          onClick={() => handleClickButton(course.title)}
                          startIcon={<RocketLaunch sx={{ fontSize: 14 }} />}
                          sx={{
                            background: `linear-gradient(135deg, ${course.color}, ${course.color}80)`,
                            color: "#ffffff",
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            px: 2.5,
                            py: 0.6,
                            borderRadius: 2,
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: `0 6px 20px ${course.color}30`,
                              background: `linear-gradient(135deg, ${course.color}80, ${course.color})`,
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          Enroll
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          disabled
                          sx={{
                            backgroundColor: "rgba(43, 173, 101, 0.1)",
                            color: "#ffffff",
                            minWidth: 80,
                          }}
                        >
                          <CircularProgress
                            size={16}
                            sx={{ color: "#2BAD65" }}
                          />
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" sx={{ color: "rgba(26, 26, 26, 0.3)" }}>
                No courses found
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(26, 26, 26, 0.2)" }}
              >
                Try searching with a different keyword
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Specialization;
